import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw, BarChart3, TrendingUp, Users, XCircle, Calendar } from 'lucide-react';
import { WaitlistAnalyticsResponse } from '../types';
import { waitlistApi } from '../services/api';

/**
 * WaitlistAnalytics - System-wide waitlist analytics dashboard for admins
 * Shows overall metrics, conversion rates, and trends
 */
function WaitlistAnalytics() {
  const [analytics, setAnalytics] = useState<WaitlistAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAnalytics = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const data = await waitlistApi.getAnalytics();
      setAnalytics(data);
    } catch (err: unknown) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const handleRefresh = () => {
    loadAnalytics(true);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number | null) => {
    if (value === null) return 'N/A';
    return value.toFixed(1);
  };

  if (isLoading && !analytics) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Analytics</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => loadAnalytics()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Waitlist Analytics</h1>
          <p className="text-gray-600">System-wide waitlist performance metrics</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Total Active Waitlists */}
        <div className="bg-gradient-to-br from-teal-50 to-moonbone border border-teal-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-primary rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-ink">Active Waitlists</p>
              <p className="text-xs text-primary-dark">Total active entries</p>
            </div>
          </div>
          <div className="text-4xl font-bold text-primary-dark">
            {analytics.total_active_waitlists.toLocaleString()}
          </div>
        </div>

        {/* Fairs with Waitlists */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-gray-600 rounded-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Active Fairs</p>
              <p className="text-xs text-gray-700">Fairs with waitlists</p>
            </div>
          </div>
          <div className="text-4xl font-bold text-gray-700">
            {analytics.total_fairs_with_waitlists.toLocaleString()}
          </div>
        </div>

        {/* Overall Conversion Rate */}
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 border border-teal-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-primary rounded-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-teal-900">Conversion Rate</p>
              <p className="text-xs text-primary-dark">Overall success rate</p>
            </div>
          </div>
          <div className="text-4xl font-bold text-primary-dark">
            {formatPercentage(analytics.overall_conversion_rate)}
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Average Position at Conversion */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Avg Position at Conversion</p>
              <p className="text-xs text-gray-600">When vendors typically convert</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-green-600">
            {analytics.average_position_at_conversion !== null
              ? `#${formatNumber(analytics.average_position_at_conversion)}`
              : 'N/A'}
          </div>
          {analytics.average_position_at_conversion !== null && (
            <p className="text-sm text-gray-600 mt-2">
              Most conversions happen near the front of the queue
            </p>
          )}
        </div>

        {/* Total Expired Offers */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Expired Offers</p>
              <p className="text-xs text-gray-600">Vendors who didn't respond in time</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-red-600">
            {analytics.total_expired_offers.toLocaleString()}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Represents missed opportunities for conversion
          </p>
        </div>
      </div>

      {/* Insights Section */}
      <div className="mt-8 bg-gradient-to-r from-teal-50 to-gray-50 border border-teal-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-secondary" />
          Key Insights
        </h2>
        <div className="space-y-3">
          {analytics.overall_conversion_rate >= 50 && (
            <div className="flex items-start gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-green-600 mt-1.5"></div>
              <p className="text-gray-700">
                <strong className="text-green-700">Strong conversion rate</strong> - More than half of notified vendors are accepting offers
              </p>
            </div>
          )}
          {analytics.overall_conversion_rate < 30 && (
            <div className="flex items-start gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-yellow-600 mt-1.5"></div>
              <p className="text-gray-700">
                <strong className="text-yellow-700">Low conversion rate</strong> - Consider reviewing offer terms or notification timing
              </p>
            </div>
          )}
          {analytics.total_expired_offers > 10 && (
            <div className="flex items-start gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-red-600 mt-1.5"></div>
              <p className="text-gray-700">
                <strong className="text-red-700">High expiration rate</strong> - Consider extending offer acceptance window or improving notifications
              </p>
            </div>
          )}
          {analytics.average_position_at_conversion !== null && analytics.average_position_at_conversion <= 3 && (
            <div className="flex items-start gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-primary mt-1.5"></div>
              <p className="text-gray-700">
                <strong className="text-primary-dark">Front-loaded conversions</strong> - Vendors at the top of the queue are most likely to convert
              </p>
            </div>
          )}
          {analytics.total_active_waitlists === 0 && (
            <div className="flex items-start gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-gray-600 mt-1.5"></div>
              <p className="text-gray-700">
                <strong className="text-gray-700">No active waitlists</strong> - All fairs have available slots
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Last Updated */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Last updated: {new Date().toLocaleString()}
      </div>
    </div>
  );
}

export default WaitlistAnalytics;
